import { z } from 'zod';

/**
 * Metrics are a narrow, deliberately boring shape: one numeric sample tied to a
 * resource + metric name + optional label set. Everything the widgets draw comes
 * from this table, which keeps plugins from each inventing a storage format.
 */

export const METRIC_UNITS = [
  'percent',
  'bytes',
  'bytes_per_sec',
  'bits_per_sec',
  'count',
  'milliseconds',
  'seconds',
  'celsius',
  'currency',
  'ratio',
  'none',
] as const;
export type MetricUnit = (typeof METRIC_UNITS)[number];

export const metricSampleSchema = z.object({
  resourceId: z.string(),
  /** Dotted namespace, e.g. "cpu.usage", "wan.rx_bps", "sip.active_calls". */
  metric: z.string().min(1),
  value: z.number().finite(),
  unit: z.enum(METRIC_UNITS).default('none'),
  /** Small label bag for series that split by interface, trunk, disk... */
  labels: z.record(z.string()).default({}),
  ts: z.number().int(),
});
export type MetricSample = z.infer<typeof metricSampleSchema>;

export const AGGREGATIONS = ['avg', 'min', 'max', 'sum', 'last', 'count'] as const;
export type Aggregation = (typeof AGGREGATIONS)[number];

export const metricQuerySchema = z.object({
  resourceId: z.union([z.string(), z.array(z.string())]).optional(),
  metric: z.string(),
  from: z.coerce.number().int(),
  to: z.coerce.number().int(),
  /** Bucket width in seconds. 0 = raw samples. */
  stepSec: z.coerce.number().int().min(0).max(86400).default(60),
  agg: z.enum(AGGREGATIONS).default('avg'),
  /** Split the result into one series per distinct value of this label. */
  groupByLabel: z.string().optional(),
});
export type MetricQuery = z.infer<typeof metricQuerySchema>;

export interface MetricSeries {
  resourceId: string;
  metric: string;
  unit: MetricUnit;
  labels: Record<string, string>;
  points: Array<[ts: number, value: number | null]>;
}

/** Retention tiers: raw for a day, 5m for a month, 1h for a year. */
export const RETENTION_TIERS = [
  { name: 'raw', stepSec: 0, keepSec: 60 * 60 * 24 },
  { name: 'm5', stepSec: 300, keepSec: 60 * 60 * 24 * 31 },
  { name: 'h1', stepSec: 3600, keepSec: 60 * 60 * 24 * 400 },
] as const;
